import React, { useState, useContext, ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HOME_PAGE } from 'settings/constant';
import { CometChatUIKit } from '@cometchat/chat-uikit-react';
import { CometChat } from '@cometchat/chat-sdk-javascript';

interface User {
  id: number;
  name: string;
  avatar: string;
  roles: string[];
}

interface AuthContextType {
  loggedIn: boolean;
  user: User | null;
  signIn: (params: any) => void;
  signUp: (params: any) => void;
  logOut: () => void;
  refreshAuthState: () => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = React.createContext<AuthContextType | undefined>(
  undefined,
);

// Custom hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const fakeUserData = {
  id: 1,
  name: 'Jhon Doe',
  avatar:
    'http://s3.amazonaws.com/redqteam.com/isomorphic-reloaded-image/profilepic.png',
  roles: ['USER', 'ADMIN'],
};

const readAuthFromStorage = (): { loggedIn: boolean; user: User | null } => {
  try {
    const jwtToken = localStorage.getItem('jwtToken');
    const userDetailsString = localStorage.getItem('userDetails');
    if (jwtToken && userDetailsString) {
      const userDetails = JSON.parse(userDetailsString);
      return {
        loggedIn: true,
        user: {
          id: userDetails.id || userDetails.userId || 0,
          name:
            `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() ||
            userDetails.name ||
            'User',
          avatar: userDetails.avatar || userDetails.profilePicture || '',
          roles: userDetails.roles || ['USER'],
        },
      };
    }
  } catch (_) {}
  return { loggedIn: false, user: null };
};

const AuthProvider = (props) => {
  let navigate = useNavigate();
  const initial = readAuthFromStorage();
  const [loggedIn, setLoggedIn] = useState(initial.loggedIn);
  const [user, setUser] = useState<User | null>(initial.user);

  const refreshAuthState = () => {
    const jwtToken = localStorage.getItem('jwtToken');
    const userDetailsString = localStorage.getItem('userDetails');

    if (jwtToken && userDetailsString) {
      try {
        const userDetails = JSON.parse(userDetailsString);
        const restoredUser: User = {
          id: userDetails.id || userDetails.userId || 0,
          name:
            `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() ||
            userDetails.name ||
            'User',
          avatar: userDetails.avatar || userDetails.profilePicture || '',
          roles: userDetails.roles || ['USER'],
        };
        setUser(restoredUser);
        setLoggedIn(true);
      } catch (error) {
        console.error('Error restoring auth state from localStorage:', error);
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('userDetails');
        setUser(null);
        setLoggedIn(false);
      }
    } else {
      setUser(null);
      setLoggedIn(false);
    }
  };

  useEffect(() => {
    refreshAuthState();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'jwtToken' || e.key === 'userDetails') {
        refreshAuthState();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const registerInCometChat = async (userData: User) => {
    try {
      const cometChatUser = new CometChat.User(userData.id.toString());
      cometChatUser.setName(userData.name);
      cometChatUser.setAvatar(userData.avatar);

      try {
        await CometChat.createUser(cometChatUser, '');
        console.log('User registered in CometChat successfully');
      } catch (createError: any) {
        if (createError.code === 'ERR_UID_ALREADY_EXISTS') {
          console.log('User already exists in CometChat, proceeding to login');
        } else {
          console.error('Error creating user in CometChat:', createError);
        }
      }

      const loggedInUser = await CometChatUIKit.login(userData.id.toString());
      console.log('User logged into CometChat successfully:', loggedInUser);

      localStorage.setItem('userDetails', JSON.stringify(userData));
    } catch (error) {
      console.error('Error with CometChat registration/login:', error);
    }
  };

  const signIn = async (params) => {
    setUser(fakeUserData);
    setLoggedIn(true);

    await registerInCometChat(fakeUserData);

    navigate(HOME_PAGE, { replace: true });
  };

  const signUp = async (params) => {
    setUser(fakeUserData);
    setLoggedIn(true);

    await registerInCometChat(fakeUserData);

    navigate('/', { replace: true });
  };

  const logOut = async () => {
    try {
      await CometChatUIKit.logout();
      console.log('User logged out from CometChat successfully');
    } catch (error) {
      console.error('Error logging out from CometChat:', error);
    }

    localStorage.removeItem('userDetails');
    localStorage.removeItem('jwtToken');

    setUser(null);
    setLoggedIn(false);
  };

  return (
    <AuthContext.Provider
      value={{
        loggedIn,
        logOut,
        signIn,
        signUp,
        user,
        refreshAuthState,
      }}
    >
      <>{props.children}</>
    </AuthContext.Provider>
  );
};

export default AuthProvider;
